<script lang="ts">
import { defineComponent, Ref, ref } from 'vue';
import { AddressesStore } from 'stores/Addresses/Addresses.ts';
import { storeToRefs } from 'pinia';
import { AddressItem } from 'src/core/types/Addresses';

export default defineComponent({
  name: 'ChangeAddressDialog',

  props: {
    modelValue: {
      required: true,
      type: Boolean,
    },
    config: Object,
  },

  computed: {
    isLoading() {
      return this.config?.loading || false;
    },
    currentAddressId() {
      return this.config?.currAddressId;
    },
  },

  setup() {
    const addressesStore = AddressesStore();
    const { addressesList } = storeToRefs(addressesStore);

    const addressesLoading = ref(false);
    const addressActionLoading: Ref<{ [addressId: number]: boolean }> = ref({});
    addressesLoading.value = true;
    addressesStore
      .getAddresses()
      .then(() => {
        addressesLoading.value = false;
      })
      .catch(() => {
        addressesLoading.value = false;
      });

    const noData = ref(false);

    return {
      addressesList,
      addressActionLoading,
      noData,
    };
  },

  methods: {
    selectAddress(address: AddressItem) {
      this.$emit('change', address);
    },
  },

  watch: {
    addressesListLoading(val) {
      if (!val && !this.addressesList.list.length) {
        this.noData = true;
      }
    },
  },
});
</script>

<template>
  <q-dialog :model-value="modelValue" @update:modelValue="$emit('update:modelValue', $event)">
    <q-card>
      <q-toolbar class="q-py-md">
        <q-avatar>
          <img src="/img/change-address.png" width="30px" />
        </q-avatar>

        <q-toolbar-title><span class="text-weight-bold">Adres Değiştir</span></q-toolbar-title>

        <q-btn flat round dense icon="close" v-close-popup />
      </q-toolbar>
      <hr class="grey q-my-none" />
      <q-card-section>
        <div v-if="addressesListLoading" class="col-12 q-pa-sm">
          <div style="width: 100%" class="text-center">
            <q-linear-progress color="primary" indeterminate class="q-mb-lg" />
            <span class="text-h6 text-weight-bold">Adresleriniz Yükleniyor</span>
          </div>
        </div>
        <div v-else ref="addressListRef" class="col-xs-12 col-md-6 q-pa-sm scroll root-container" :key="key" v-for="(address, key) in addressesList.list">
          <q-intersection transition="scale" once class="example-item">
            <q-card flat bordered class="my-card">
              <q-card-section class="bg-amber-2">
                <div class="row items-center no-wrap">
                  <div class="col">
                    <div class="text-h6 text-weight-bold flex items-center">
                      <span class="q-mr-md">{{ address.name }}</span>
                    </div>
                    <div class="text-subtitle2">{{ address.namesurname }}</div>
                  </div>

                  <div class="col-auto">
                    <q-btn
                      v-if="currentAddressId !== address.id"
                      size="small"
                      rounded
                      unelevated
                      color="primary"
                      @click="selectAddress(address)"
                      :loading="isLoading"
                      no-caps
                      >Seç
                    </q-btn>
                  </div>
                </div>
              </q-card-section>

              <q-card-section class="bg-amber-1">
                <span class="text-grey-8">Mahalle:</span>
                {{ address.neighbourhood }}
                <br />
                <span class="text-grey-8">Adres:</span>
                {{ address.address }}
                <span v-show="address.postcode">- {{ address.postcode }},</span>
                {{ address.district }}/{{ address.city }}
                <br />
                <span class="text-grey-8">Gsm:</span>
                {{ address.telephone }}
                <br />
                <br />
                <span class="text-grey-8">Fatura Türü:</span>
                {{ address.type === 1 ? 'Bireysel' : 'Kurumsal' }}
                <br />
                <span v-if="address.type === 1">
                  <span class="text-grey-8">T.C.:</span>
                  {{ address.citizenshipnumber }}
                </span>
                <span v-else>
                  <span class="text-grey-8">Firma Adı:</span>
                  {{ address.companyname }}
                  <br />
                  <span class="text-grey-8">Vergi Numarası:</span>
                  {{ address.taxnumber }}
                  <br />
                  <span class="text-grey-8">Vergi Dairesi:</span>
                  {{ address.taxoffice }}
                </span>
              </q-card-section>
              <q-separator />
            </q-card>
          </q-intersection>
        </div>

        <div v-if="noData && !addressesList.list.length" class="col-12 q-pa-sm">Sistemde kayıtlı adresiniz bulunmuyor, hemen yeni adres ekleyebilirsiniz</div>
      </q-card-section>
    </q-card>
  </q-dialog>
</template>

<style scoped></style>
